'use client';

/**
 * API namespace `interviews`.
 * Verbatim extraction from frontend/src/lib/api-client.ts (L4733-L4876).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const interviews = {
  /**
   * POST /interviews — create an interview room.
   *
   * Spec ref: SPEC-024 §3.1 (create), SPEC-NEGOCIO §1.2 (interview room:
   * max 2 presenters, 0 spectators, invitation-only access).
   *
   * The backend returns the created room shape (10 explicit fields
   * picked from the `IRoom` document — see
   * `backend/src/controllers/interview.controller.ts:79-92`).
   */
  create(body: CreateInterviewBody, signal?: AbortSignal): Promise<InterviewCreatedResponse> {
    return apiFetch<InterviewCreatedResponse>('/interviews', {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    });
  },
  /**
   * GET /interviews — list the current user's interview rooms.
   *
   * The backend strips sensitive fields (invitationToken,
   * invitationExpiresAt, passwordHash, participants) from the list
   * response per AUDIT2-013 — the host fetches a fresh invitation
   * via `api.interviews.invite(roomId)` when they actually want to
   * share it.
   *
   * The list is NOT paginated (returns all of the user's interview
   * rooms — expected to be small per user).
   */
  list(signal?: AbortSignal): Promise<InterviewListResponse> {
    return apiFetch<InterviewListResponse>('/interviews', { signal });
  },
  /**
   * POST /interviews/:roomId/questions — set the pre-configured
   * interview questions.
   *
   * Spec ref: SPEC-024 §3.4 (questions), SPEC-NEGOCIO §1.2
   * (Pre-configuración de preguntas).
   *
   * The backend enforces max 20 questions × 500 chars each
   * (`setQuestionsSchema` in
   * `backend/src/controllers/interview.controller.ts:41-43`).
   * Returns `{ updated: true }` on success.
   *
   * Authz: host-only (or admin). Non-host callers get 403.
   */
  setQuestions(roomId: string, questions: string[], signal?: AbortSignal): Promise<{ updated: boolean }> {
    return apiFetch<{ updated: boolean }>(
      `/interviews/${encodeURIComponent(roomId)}/questions`,
      {
        method: 'POST',
        body: JSON.stringify({ questions }),
        signal,
      },
    );
  },
  /**
   * POST /interviews/:roomId/invite — generate a fresh invitation.
   *
   * Returns the invitation URL + opaque token + expiry (24h per
   * `INTERVIEW_INVITATION_EXPIRY_HOURS` in the interview service).
   *
   * NOTE: the backend handler ignores `body` entirely (the invitation
   * is generated server-side from the room record). The `body` param
   * is kept for forward-compat (e.g., if the backend later accepts
   * `expiresInHours` for per-invitation expiry).
   *
   * Authz: host-only (or admin). Non-host callers get 403.
   */
  invite(roomId: string, body?: InviteBody, signal?: AbortSignal): Promise<InviteResponse> {
    // `body` is typed but currently ignored by the backend — see
    // interview.controller.ts:242-269. Pass it through anyway so a
    // future backend change doesn't require a frontend signature
    // change.
    return apiFetch<InviteResponse>(
      `/interviews/${encodeURIComponent(roomId)}/invite`,
      {
        method: 'POST',
        body: body ? JSON.stringify(body) : JSON.stringify({}),
        signal,
      },
    );
  },
  /**
   * POST /interviews/:roomId/join — join an interview via invitation.
   *
   * The `invitationToken` is an opaque 64-hex-char string
   * (`crypto.randomBytes(32).toString('hex')` — see M-09 fix). The
   * backend compares it as a string + checks `invitationExpiresAt`
   * (24h TTL). No JWT signature verification (intentional — the token
   * is opaque per the M-09 fix).
   *
   * Authz: any authenticated user with a valid invitation token.
   * Returns the participant record (userId, role, joinedAt,
   * handRaised, connectionStatus).
   */
  join(roomId: string, body: JoinBody, signal?: AbortSignal): Promise<JoinResponse> {
    return apiFetch<JoinResponse>(
      `/interviews/${encodeURIComponent(roomId)}/join`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        signal,
      },
    );
  },
};
